import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import capitalize from 'lodash/capitalize';
import kebabCase from 'lodash/kebabCase';
import { Text } from '@atlaskit/primitives';
import Badge from '@atlaskit/badge';
import classNames from 'classnames';
import AddIcon from '@atlaskit/icon/glyph/add';
import ExportIcon from '@atlaskit/icon/glyph/export';
import Button, { LoadingButton } from '@atlaskit/button';
import Lozenge from '@atlaskit/lozenge';
import DropdownMenu, { DropdownItemRadio, DropdownItemGroupRadio } from '@atlaskit/dropdown-menu';
import {
  InvoiceGroup,
  PaymentMethodType,
  PaymentOnTerms,
  FilterOption,
  PaymentMethodTypeV2,
} from '@css-xp/graphtypes/src/generated-types-new';
import { NEW_DATE_FORMAT } from '@atlassian/cssxp-typescommon';
import {
  errorStateProp,
  emptyStateProp,
  getEmptyStatePropForBillingProfileTable,
  getSelectedColumns,
  NoData,
  TableHeading,
  SoldToAddress,
} from '@css-xp/ppc-common';
import { DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE } from '@atlassian/cssxp-utils-entitlement';
import { CommerceAddress } from '@atlassian/cssxp-graphtypes/src/generated-types-new';
import {
  CustomizeView,
  DynamicResultTable,
  errorNotificationBuilder,
  FormattedDate,
  HeadCell,
  NotificationTypes,
  useFeatureToggle,
  useNotification,
  MoreActionsItem,
  OpenSidebarPayload,
  useSidebar,
  MoreActions,
  TabPanel,
  Tabs,
  CustomSearch,
  CopyableText,
  ActiveLozenge,
  InactiveLozenge,
  AchMethodInTable,
} from '@css-xp/base';
import { formatPaymentMethod, getInvoiceGroupName, getTaxContent } from '@css-xp/billing-v2';
import { CreateBillingProfileModalV2 } from './modal/create-billing-profile-modal-v2';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, useAdvocateAccessControl } from '@css-xp/ppc-access-control';
import {
  routePaths,
  defaultBillingProfileSortOptions,
  BillingProfileTableKeys,
  ENABLE_DESIGN_ITERATION_1,
  SUBSCRIPTION_CONSTANTS,
  STATSIG_FEATURE_GATES,
} from '@css-xp/ppc-constants';
import { getRouteUrl } from '@css-xp/ppc-utils';
import { useOpenClose } from '@css-xp/ppc-custom-hooks';
import { SortDirection } from '@css-xp/ppc-graphtypes';
import { EndCustomerContacts, ContactType } from '@css-xp/contact-resources';
import { PaypalPaymentMethodInTable } from '@css-xp/ppc-paypal';
import { useBillingProfilesCSV } from './hooks/use-billing-profiles-csv';
import { useFetchBillingProfilesForTransactionAccountQuery } from './hooks/use-fetch-billing-profiles-for-tansaction-account-ppc';
import { useFetchInternalBillingProfilesForTransactionAccountQuery } from './hooks/use-fetch-internal-billing-profiles-for-transaction-account-ppc';
import './billing-profile-table.scss';

type BillingProfileTableProps = {
  transactionAccountId: string;
  soldToAddressIds?: string[];
  label?: string;
  defaultSelectedAddress?: CommerceAddress;
  customerAddresses?: CommerceAddress[];
  isWildcardSearchEnabled?: boolean;
  onAddBillingProfile?: () => void;
  setRefetchBillingProfiles?: (_refetch: () => void) => void;
  shouldFetchInternalLicenses?: boolean;
};

enum SIDEBAR {
  CREATE_BILLING_PROFILE = 'createBillingProfilePane',
}

type ColumnList = HeadCell;

type FormData = {
  searchTerm: string;
};

const getFilterOptions = (
  selectedTab?: SUBSCRIPTION_CONSTANTS.TAB_ENUM,
  isRefetch?: boolean,
  soldToAddressIds?: string[]
) => {
  const filterOptions: FilterOption[] = [];

  if (selectedTab === SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE) {
    filterOptions.push({
      key: 'not',
      // to support old data null is also considered as Active BP status fix suggested by DE team
      value: JSON.stringify([{ status: SUBSCRIPTION_CONSTANTS.TAB_ENUM.INACTIVE }]),
    });
  } else if (selectedTab === SUBSCRIPTION_CONSTANTS.TAB_ENUM.INACTIVE) {
    filterOptions.push({
      key: 'status',
      value: SUBSCRIPTION_CONSTANTS.TAB_ENUM.INACTIVE,
    });
  }

  if (isRefetch) {
    const value = JSON.stringify(soldToAddressIds?.map((shipToId) => ({ shipToId })));
    value &&
      filterOptions.push({
        key: 'or',
        // sending multiple ship-to ids
        value: value,
      });
  }
  return filterOptions;
};

export const BillingProfileTable = ({
  transactionAccountId,
  soldToAddressIds,
  label,
  defaultSelectedAddress,
  customerAddresses,
  isWildcardSearchEnabled,
  onAddBillingProfile,
  setRefetchBillingProfiles,
  shouldFetchInternalLicenses,
}: BillingProfileTableProps) => {
  const [isEnabled] = useFeatureToggle();
  const isDesignIteration1Enabled = isEnabled(ENABLE_DESIGN_ITERATION_1);
  const isRedesignFastFollowEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_REDESIGN_FAST_FOLLOW);
  const filterOptions = useRef<FilterOption[]>(getFilterOptions(SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE));
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [sortOrder, setSortOrder] = useState<SortDirection>(SortDirection.ASC);
  const [selectedTab, setSelectedTab] = useState<string | undefined>(SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE);
  const [isActiveStatusOptionSelected, setIsActiveStatusOptionSelected] = useState<boolean>(true);
  const { sendErrorNotification, notificationSent, setNotificationSent } = useNotification();
  const { openSidebar } = useSidebar();

  const {
    isOpen: showCreateBillingProfileModal,
    open: openCreateBillingProfileModal,
    close: closeCreateBillingProfileModal,
  } = useOpenClose();

  const advocateHasAccess = useAdvocateAccessControl({
    accessType: ADVOCATE_ACCESS_TYPE.CREATE,
    resource: ADVOCATE_RESOURCE_TYPE.INVOICE_GROUP,
  });

  const handleSidebarAction = (openSidebarPayload: OpenSidebarPayload) => {
    openSidebar(openSidebarPayload);
  };

  const {
    refetch: refetchBillingProfiles,
    data: billingProfilesData,
    error: billingProfilesError,
    loading: billingProfilesForTXALoading,
  } = useFetchBillingProfilesForTransactionAccountQuery({
    soldToAddressIds: soldToAddressIds,
    transactionAccountId: transactionAccountId,
    sortOptions: defaultBillingProfileSortOptions,
    filterOptions: getFilterOptions(SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE),
    skipOption: shouldFetchInternalLicenses,
  });

  const {
    refetchInternalBillingProfiles,
    internalBillingProfilesData,
    internalBillingProfilesError,
    internalBillingProfilesLoading,
  } = useFetchInternalBillingProfilesForTransactionAccountQuery({
    transactionAccountId: transactionAccountId,
    sortOptions: defaultBillingProfileSortOptions,
    filterOptions: getFilterOptions(SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE),
    skipOption: !shouldFetchInternalLicenses,
  });

  const refetch = shouldFetchInternalLicenses ? refetchInternalBillingProfiles : refetchBillingProfiles;
  const data = shouldFetchInternalLicenses ? internalBillingProfilesData : billingProfilesData;
  const error = shouldFetchInternalLicenses ? internalBillingProfilesError : billingProfilesError;
  const loading = shouldFetchInternalLicenses ? internalBillingProfilesLoading : billingProfilesForTXALoading;

  const nodesData = shouldFetchInternalLicenses
    ? // @ts-ignore
      data?.transactionAccount?.internalUseBillingProfiles?.nodes
    : // @ts-ignore
      data?.transactionAccount?.billingProfiles?.nodes;
  const totalCountData = shouldFetchInternalLicenses
    ? // @ts-ignore
      data?.transactionAccount?.internalUseBillingProfiles?.totalCount
    : // @ts-ignore
      data?.transactionAccount?.billingProfiles?.totalCount;

  // variables to use for paging and populating our template
  const totalCount = totalCountData ?? 0;
  const results = nodesData ?? [];

  const { downloadBillingProfilesCSV, billingProfilesLoading } = useBillingProfilesCSV(
    transactionAccountId,
    soldToAddressIds,
    selectedTab,
    undefined,
    shouldFetchInternalLicenses
  );

  const onSearch = useCallback((formData: FormData) => {
    const currentSearchOptions = [];
    const searchInput = formData?.searchTerm;
    if (searchInput) {
      const searchKeys = [
        'name',
        'invoiceGroupNumber',
        'shipToName',
        'shipToAddress1',
        'shipToAddress2',
        'shipToCity',
        'shipToCountry',
        'shipToPostalCode',
        'shipToState',
      ];
      // this will enable the wild-card search for BPs
      if (isWildcardSearchEnabled) {
        searchKeys.push('nameReversePrefix');
        searchKeys.push('namePrefix');
      }
      currentSearchOptions.push({
        key: 'or',
        value: JSON.stringify(searchKeys.map((key) => ({ [key]: searchInput }))),
      });
    }
    setCurrentPage(DEFAULT_PAGE);

    const soldToIdsValue = JSON.stringify(soldToAddressIds?.map((shipToId) => ({ shipToId })));
    const defaultFilter = soldToIdsValue
      ? [
          {
            key: 'or',
            value: soldToIdsValue,
          },
        ]
      : [];

    refetch({
      filterOptions: [
        ...filterOptions.current,
        {
          and: [...defaultFilter, ...currentSearchOptions],
        },
      ],
    });
  }, []);

  const onPageChange = (newPage: number) => {
    if (currentPage === newPage) {
      return;
    }
    setCurrentPage(newPage);
    refetch({
      id: transactionAccountId,
      searchOptions: {
        offset: newPage !== 1 ? newPage * rowsPerPage - rowsPerPage : 0,
        first: rowsPerPage,
        sort: defaultBillingProfileSortOptions,
      },
    });
  };

  const onRowPerPageChange = (newRowPerPage: number) => {
    if (newRowPerPage === rowsPerPage) {
      return;
    }
    setCurrentPage(1);
    setRowsPerPage(newRowPerPage);
    refetch({
      id: transactionAccountId,
      searchOptions: {
        offset: 0,
        first: newRowPerPage,
        sort: defaultBillingProfileSortOptions,
      },
    });
  };

  const isDesignIteration1EnabledAndNoData = isDesignIteration1Enabled && !totalCount;
  const isDesignIteration1EnabledWithData = isDesignIteration1Enabled && totalCount !== 0;
  const showPagination = totalCount > DEFAULT_ROWS_PER_PAGE;

  const wrapInCell = (node: React.ReactElement | string) => {
    return <div>{node}</div>;
  };

  const getPayByStatementValue = useMemo(() => {
    return (billingProfile: InvoiceGroup | undefined | null): boolean => {
      const paymentMethod = billingProfile?.defaultPaymentMethod?.type;
      if (paymentMethod === PaymentMethodType.CARD) return false;
      if (paymentMethod === PaymentMethodType.DEFERRED) {
        const deferredPaymentMethod = billingProfile?.defaultPaymentMethod as PaymentOnTerms;
        return !!deferredPaymentMethod?.terms?.payOnStatement;
      }
      return false;
    };
  }, [results]);

  const renderCurrencyContent = (billingProfile: InvoiceGroup | undefined | null) => {
    return wrapInCell(billingProfile?.currency ?? <NoData testId="no-currency-found" />);
  };
  const renderCustomerContactsContent = (
    billingProfile: InvoiceGroup | undefined | null,
    contactType: ContactType = ContactType.CUSTOMER_CONTACT
  ) => {
    return wrapInCell(
      <EndCustomerContacts
        invoiceGroupId={billingProfile?.id ?? ''}
        transactionAccountId={transactionAccountId}
        contactType={contactType}
        shouldHidePrimaryContact={contactType === ContactType.BILLING_CONTACT}
        shouldRenderForInternalUse={shouldFetchInternalLicenses}
      />
    );
  };

  const renderStatusContent = (billingProfile: InvoiceGroup | undefined | null) => {
    return wrapInCell(billingProfile?.isActive ? <ActiveLozenge /> : <InactiveLozenge />);
  };

  const initialRows = results.map((billingProfile: any, index: number) => {
    const hasPayOnStatement = getPayByStatementValue(billingProfile);
    //@ts-ignore
    const hasPayPalPaymentMethod = billingProfile?.defaultPaymentMethod?.type === PaymentMethodTypeV2.PAYPAL;
    //@ts-ignore
    const hasAchPaymentMethod = billingProfile?.defaultPaymentMethod?.type === PaymentMethodTypeV2.ACH;

    const billingProfileId = wrapInCell(
      billingProfile?.id ? (
        <>
          <Link
            to={`${getRouteUrl(routePaths.billingProfileDetails, {
              txaId: transactionAccountId,
              billingProfileId: `${encodeURIComponent(billingProfile?.id ?? '')}`,
            })}`}
            onClick={(e) => e.stopPropagation()}
            className="billing-profile-name"
            data-testid="billing-profile-name-link"
          >
            {getInvoiceGroupName(billingProfile?.name)}
            {billingProfile?.defaultPaymentMethod?.paymentIssue &&
              billingProfile?.defaultPaymentMethod?.type !== PaymentMethodType.DEFERRED && (
                <Lozenge appearance="removed">PAYMENT ERROR</Lozenge>
              )}
          </Link>
          {isDesignIteration1Enabled && (
            <Text size="small" color="color.text.subtlest">
              <CopyableText text={billingProfile?.invoiceGroupNumber || billingProfile?.id} />
            </Text>
          )}
        </>
      ) : (
        <NoData testId="billing-id-not-found" />
      )
    );

    const soldToAddressTd = wrapInCell(
      <div className="line-clamp">
        <SoldToAddress
          shipTo={billingProfile?.shipToParty}
          {...(isDesignIteration1Enabled ? { hasLineBreak: false, shouldDisableLink: true } : {})}
        />
      </div>
    );

    let payOnAccountTd: Element | JSX.Element | undefined = <NoData testId="payment-method-not-found" />;

    if (hasPayOnStatement) {
      payOnAccountTd = <Lozenge appearance="new">Pay on account</Lozenge>;
    } else if (hasPayPalPaymentMethod) {
      // @ts-ignore
      payOnAccountTd = <PaypalPaymentMethodInTable emailID={billingProfile?.defaultPaymentMethod?.payPal?.email} />;
    } else if (hasAchPaymentMethod) {
      // @ts-ignore
      payOnAccountTd = (
        <AchMethodInTable
          bankName={billingProfile?.defaultPaymentMethod?.ach?.bankName}
          last4={billingProfile?.defaultPaymentMethod?.ach?.last4}
        />
      );
    } else if (billingProfile?.defaultPaymentMethod) {
      payOnAccountTd = formatPaymentMethod(billingProfile?.defaultPaymentMethod);
    }

    const currencyTd = renderCurrencyContent(billingProfile);

    const customerContactsTd = renderCustomerContactsContent(billingProfile);

    const dateTd = <FormattedDate date={billingProfile?.createdAt} format={NEW_DATE_FORMAT} />;

    const txaContentTd = getTaxContent(billingProfile as InvoiceGroup);

    const pricingEligibilityTd = wrapInCell(
      billingProfile?.shipToPriceEligibility ? (
        capitalize(billingProfile?.shipToPriceEligibility)
      ) : (
        <div aria-label="Not applicable">Commercial</div>
      )
    );

    const invoiceGroupNumberTd = wrapInCell(
      (billingProfile?.invoiceGroupNumber || billingProfile?.id) ?? <NoData testId="no-number-found" />
    );

    const billingAdminsTd = renderCustomerContactsContent(billingProfile, ContactType.BILLING_CONTACT);

    const cells = isDesignIteration1Enabled
      ? [
          {
            key: BillingProfileTableKeys.NAME,
            content: billingProfileId,
          },
          ...(isRedesignFastFollowEnabled
            ? [
                {
                  content: renderStatusContent(billingProfile),
                },
              ]
            : []),
          {
            content: soldToAddressTd,
          },
          {
            content: payOnAccountTd,
          },
          {
            content: currencyTd,
          },
          {
            content: customerContactsTd,
          },
          {
            content: billingAdminsTd,
          },
        ].filter(Boolean)
      : [
          {
            key: BillingProfileTableKeys.NAME,
            content: billingProfileId,
          },
          {
            content: soldToAddressTd,
          },
          {
            content: payOnAccountTd,
          },
          {
            content: customerContactsTd,
          },
          {
            content: currencyTd,
          },
          {
            content: dateTd,
          },
          {
            content: txaContentTd,
          },
          {
            content: pricingEligibilityTd,
          },
          {
            content: invoiceGroupNumberTd,
          },
        ].filter(Boolean);

    return {
      key: billingProfile?.id ?? index.toString(),
      cells,
    };
  });

  const [tableRows, setTableRows] = useState(initialRows);

  const onSort = () => {
    const newSortedData = [...tableRows].reverse();
    setTableRows(newSortedData);
    setSortOrder(sortOrder === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC);
  };

  const getHead = () => {
    if (isDesignIteration1Enabled) {
      const colHeads = [
        {
          key: BillingProfileTableKeys.NAME,
          isSortable: true,
          content: 'Billing profile',
          width: 20,
        },
        ...(isRedesignFastFollowEnabled
          ? [
              {
                content: 'Status',
                width: 10,
              },
            ]
          : []),
        {
          content: 'Sold-to address',
          width: 35,
        },
        {
          content: 'Payment method',
          width: 20,
        },
        {
          content: 'Currency',
          width: 10,
        },
        {
          content: shouldFetchInternalLicenses ? 'Contacts' : 'Customer contacts',
          width: 25,
        },
      ];
      if (!shouldFetchInternalLicenses) {
        colHeads.push({
          content: 'Billing admins',
          width: 25,
        });
      }

      return colHeads;
    }
    return [
      {
        key: BillingProfileTableKeys.NAME,
        isSortable: true,
        content: 'Name',
        width: 15,
      },
      {
        content: 'Sold-to address',
        width: 20,
      },
      {
        content: 'Payment method',
        width: 20,
      },
      {
        content: 'Customer contacts',
        width: 20,
      },
      {
        content: 'Currency',
        width: 10,
      },
      {
        key: 'created-on',
        content: 'Created on',
        isMandatory: false,
        isChecked: true,
        width: 10,
      },
      {
        content: 'Tax ID',
        isMandatory: false,
        isChecked: true,
        width: 15,
      },
      {
        content: 'Price eligibility',
        width: 10,
      },
      {
        content: 'Number',
        width: 15,
      },
    ];
  };

  if (error) {
    if (!notificationSent) {
      const errorMessage = errorNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withHeader(`Could not fetch the latest data`)
        .withMessage(`Please wait for a moment and then refresh the page.`)
        .withDismissAfter(5000)
        .build();
      sendErrorNotification(errorMessage);
      setNotificationSent(true);
    }
  }

  useEffect(() => {
    setTableRows(initialRows);
  }, [nodesData]);

  useEffect(() => {
    if (isDesignIteration1Enabled && refetch) {
      setRefetchBillingProfiles?.(() => refetch);
    }
  }, []);

  const [columnsList, setColumnList] = useState<Array<ColumnList>>(getHead());

  const handleAddBillingProfile = () => {
    if (shouldFetchInternalLicenses) {
      return openCreateBillingProfileModal();
    }

    return handleSidebarAction({
      sidebarType: SIDEBAR.CREATE_BILLING_PROFILE,
      payload: {
        transactionAccountId: transactionAccountId,
        //  Used polling because after successful form submission hydra is taking time to
        //  update the data due to which we are unable to show data just after refetch.
        refetch: refetch,
        defaultSoldToAddress: defaultSelectedAddress,
        soldToAddresses: customerAddresses,
        isDefaultAddressSelected: !!defaultSelectedAddress,
        isProgressIndicatorEnabled: isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES),
      },
      sidebarOptions: {
        sidebarWidth: '600px',
        isOverlay: true,
      },
    });
  };

  const customerActions = [
    ...(advocateHasAccess && !isDesignIteration1Enabled
      ? [
          {
            id: 'billing_profile',
            content: 'Add billing profile',
            action: onAddBillingProfile ? onAddBillingProfile : handleAddBillingProfile,
          },
        ]
      : []),
    {
      id: 'export_as_csv',
      content: (
        <>
          <ExportIcon label="Export icon" size="small" /> Export as CSV
        </>
      ),
      isDisabled: billingProfilesLoading,
      action: () => downloadBillingProfilesCSV(),
    },
  ];

  const renderStatusFilter = () => {
    if (!isDesignIteration1Enabled) {
      return null;
    }
    const dropdownMenuTrigger = (
      <>
        Status{' '}
        <span className="pl-4">
          <Badge appearance="primary">1</Badge>
        </span>
      </>
    );

    return (
      <div className="ml-8">
        <DropdownMenu trigger={dropdownMenuTrigger} triggerType="button">
          <DropdownItemGroupRadio id="status-filter-options">
            <DropdownItemRadio
              id="active"
              isSelected={isActiveStatusOptionSelected}
              onClick={() => {
                setIsActiveStatusOptionSelected(true);
                onTabSelect(SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE);
              }}
            >
              Active
            </DropdownItemRadio>
            <DropdownItemRadio
              id="inactive"
              isSelected={!isActiveStatusOptionSelected}
              onClick={() => {
                setIsActiveStatusOptionSelected(false);
                onTabSelect(SUBSCRIPTION_CONSTANTS.TAB_ENUM.INACTIVE);
              }}
            >
              Inactive
            </DropdownItemRadio>
          </DropdownItemGroupRadio>
        </DropdownMenu>
      </div>
    );
  };

  const onTabSelect = async (tabLabel: string) => {
    const newTab = tabLabel as SUBSCRIPTION_CONSTANTS.TAB_ENUM;
    setSelectedTab(newTab);
    setTableRows(initialRows);
    setSortOrder(SortDirection.ASC);

    const newFilterOptions: FilterOption[] = getFilterOptions(newTab, true, soldToAddressIds);

    filterOptions.current = newFilterOptions;
    setCurrentPage(DEFAULT_PAGE);
    refetch({
      filterOptions: newFilterOptions,
      searchOptions: {
        first: rowsPerPage,
        offset: 0,
        sort: defaultBillingProfileSortOptions,
      },
    });
  };

  const renderBeforeTable = () => {
    if (isDesignIteration1EnabledWithData) {
      return label ? <TableHeading className="pt-32 ph-28">{label}</TableHeading> : null;
    }
    return null;
  };

  const renderCustomizeView = () => {
    if (isDesignIteration1Enabled) {
      return null;
    }

    return (
      <CustomizeView
        columns={columnsList}
        columnCount={columnsList.length}
        updatedColumnView={(record: Array<HeadCell>) => setColumnList([...record] as Array<ColumnList>)}
      />
    );
  };

  const renderAddBillingProfile = () => {
    if (!isDesignIteration1Enabled || !advocateHasAccess) {
      return null;
    }

    return (
      <Button
        testId="add-billing-profile-button"
        iconBefore={<AddIcon label="Add" size="small" />}
        onClick={onAddBillingProfile ? onAddBillingProfile : handleAddBillingProfile}
      >
        Billing profile
      </Button>
    );
  };

  const renderActions = () => {
    if (isRedesignFastFollowEnabled && customerActions.length === 1) {
      return (
        <LoadingButton
          appearance="default"
          onClick={customerActions[0].action}
          testId={customerActions[0].id}
          isLoading={customerActions[0].isDisabled}
          isDisabled={customerActions[0].isDisabled}
        >
          {customerActions[0].content}
        </LoadingButton>
      );
    }

    return (
      <MoreActions
        actions={customerActions as MoreActionsItem[]}
        isLoading={isDesignIteration1Enabled && billingProfilesLoading}
      />
    );
  };

  const renderTabTable = () => {
    return (
      <DynamicResultTable
        beforeTable={renderBeforeTable()}
        testId="billing-profiles"
        customiseColumnView={
          <div className="billing-profiles-right-action-container">
            {renderCustomizeView()}
            {isDesignIteration1EnabledAndNoData || renderAddBillingProfile()}
            {isDesignIteration1EnabledAndNoData || renderActions()}
          </div>
        }
        head={isDesignIteration1EnabledAndNoData ? { cells: [] } : { cells: getSelectedColumns(columnsList) }}
        rows={tableRows}
        emptyStateProps={
          isDesignIteration1Enabled
            ? getEmptyStatePropForBillingProfileTable(renderAddBillingProfile())
            : emptyStateProp
        }
        isLoading={loading}
        totalCount={totalCount}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        isPaginationEnabled={showPagination}
        onRowChange={onRowPerPageChange}
        error={error?.message}
        errorStateProps={errorStateProp}
        showFilters={isDesignIteration1Enabled}
        {...(totalCount > 0 ? { sortKey: BillingProfileTableKeys.NAME, sortOrder, onSort } : {})}
      >
        <div className="billing-profile-table-search-container">
          <CustomSearch
            placeholder="Search"
            onSearch={onSearch}
            isLoading={loading}
            isClearable={true}
            helperMessage="Enter IG-#, billing profile name or sold-to address"
          />
        </div>

        {renderStatusFilter()}
      </DynamicResultTable>
    );
  };
  return (
    <div
      className={classNames('contact-billing-account-container-v2', {
        'tab-container-billing-profile': !isDesignIteration1Enabled,
        'tab-container-with-label': label,
      })}
    >
      {shouldFetchInternalLicenses && (
        <CreateBillingProfileModalV2
          isOpen={showCreateBillingProfileModal}
          transactionAccountId={transactionAccountId}
          onClose={closeCreateBillingProfileModal}
          shouldRenderForInternalUse={true}
          existingBillingProfileDetails={nodesData}
          refetchBillingProfiles={refetch}
        />
      )}
      {label && !isDesignIteration1Enabled && <TableHeading className="pt-32 ph-28">{label}</TableHeading>}
      {isDesignIteration1Enabled ? (
        renderTabTable()
      ) : (
        <Tabs testId="subscription-table-tabs" onTabSelect={onTabSelect} selectedTabKey={kebabCase(selectedTab)}>
          <TabPanel label={SUBSCRIPTION_CONSTANTS.TAB_ENUM.ACTIVE}>{renderTabTable()}</TabPanel>
          <TabPanel label={SUBSCRIPTION_CONSTANTS.TAB_ENUM.INACTIVE}>{renderTabTable()}</TabPanel>
        </Tabs>
      )}
    </div>
  );
};
